import type { DataSource } from '@/src/renderer/lib/dataSource'
import type { LifeSpace, Task, TaskCascade, TaskChanges } from '@/src/renderer/lib/db/models'
import { computeAppendOrders } from '../../../utils/appendTaskOrders'
import { buildTaskUpdate, type StagedAction } from '../../../utils/buildTaskUpdate'
import { newProjectInput } from '../../../utils/newProjectInput'

type CommitSource = Pick<
  DataSource,
  'getTask' | 'getTasksByProject' | 'getSectionsByProject' | 'updateTasks' | 'deleteTask' | 'createProject' | 'deleteProject'
>

type Move = Extract<StagedAction, { kind: 'move' }>

export interface ClarifyCommitResult {
  /** Die gelöschten Einträge, gelesen VOR dem Löschen — das Rückgängig legt sie so zurück. */
  snapshots: Task[]
  cascades: Map<string, TaskCascade>
}

/** „Garten“ und „garten “ im selben Lebensbereich meinen dasselbe Projekt. */
const newProjectKey = (name: string, lifeSpace: LifeSpace) =>
  `${lifeSpace}:${name.trim().replace(/\s+/g, ' ').toLocaleLowerCase()}`

/**
 * **Bucht das Einsortieren** (K) — in der Reihenfolge der Liste.
 *
 * 1. **Neue Projekte erst jetzt** (#267): Wer das Einsortieren mit Esc verlässt, hinterlässt keines.
 *    Gleicher Name im selben Lebensbereich wird EIN Projekt.
 * 2. **Verschieben hängt ans Ende des Ziels**, wie überall (`moveTasks`) — bis #267 behielt die
 *    Aufgabe ihre `order` aus Kurz notiert und landete irgendwo im Projekt.
 * 3. Alle Updates auf einmal: Scheitert es, bleibt nichts halb einsortiert, nichts gelöscht — und die
 *    eben angelegten, noch leeren Projekte gehen wieder.
 * 4. **Löschen wie in der Liste** (#224): `deleteTask` nimmt Unteraufgaben, Kommentare, Erinnerungen
 *    und Fokuszeit mit und meldet die Löschung der Cloud.
 */
export async function commitClarify(
  source: CommitSource,
  entries: ReadonlyArray<{ taskId: string; action: StagedAction }>,
  now: Date,
): Promise<ClarifyCommitResult> {
  const deleteIds = entries.filter(entry => entry.action.kind === 'delete').map(entry => entry.taskId)
  const snapshots = (await Promise.all(deleteIds.map(id => source.getTask(id))))
    .filter((task): task is Task => task != null)

  const created: string[] = []
  try {
    const projectIds = new Map<string, string>()
    const moves = new Map<string, Move>()
    for (const { taskId, action } of entries) {
      if (action.kind === 'move') moves.set(taskId, action)
      if (action.kind !== 'newProject') continue
      const key = newProjectKey(action.name, action.lifeSpace)
      let projectId = projectIds.get(key)
      if (!projectId) {
        projectId = (await source.createProject(newProjectInput(action.name, action.lifeSpace))).id
        projectIds.set(key, projectId)
        created.push(projectId)
      }
      moves.set(taskId, { kind: 'move', projectId, sectionId: null, lifeSpace: action.lifeSpace, place: action.name })
    }

    const orders = await appendOrders(source, moves)
    const updates: Array<{ id: string; changes: TaskChanges }> = []
    for (const { taskId, action } of entries) {
      if (action.kind === 'delete') continue
      const resolved = action.kind === 'newProject' ? moves.get(taskId)! : action
      const changes = buildTaskUpdate(resolved, now)
      const order = orders.get(taskId)
      updates.push({ id: taskId, changes: order === undefined ? changes : { ...changes, order } })
    }
    await source.updateTasks(updates)
  } catch (error) {
    for (const id of created) await source.deleteProject(id)
    throw error
  }

  const cascades = new Map<string, TaskCascade>()
  for (const id of deleteIds) cascades.set(id, await source.deleteTask(id))
  return { snapshots, cascades }
}

/** Je Ziel einmal gelesen; was dorthin wandert, steht in der Reihenfolge der Liste dahinter. */
async function appendOrders(source: CommitSource, moves: ReadonlyMap<string, Move>): Promise<Map<string, number>> {
  const byTarget = new Map<string, string[]>()
  for (const [taskId, move] of moves) {
    const key = `${move.projectId}\u0000${move.sectionId ?? ''}`
    byTarget.set(key, [...(byTarget.get(key) ?? []), taskId])
  }
  const orders = new Map<string, number>()
  for (const taskIds of byTarget.values()) {
    const { projectId, sectionId } = moves.get(taskIds[0]!)!
    const [tasks, sections] = await Promise.all([source.getTasksByProject(projectId), source.getSectionsByProject(projectId)])
    const moving = new Set(taskIds)
    computeAppendOrders({ tasks: tasks.filter(task => !moving.has(task.id)), sections, sectionId, count: taskIds.length })
      .forEach((order, i) => orders.set(taskIds[i]!, order))
  }
  return orders
}
